import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authApi } from '@/auth/authApi';
import { paths } from '@/routes/paths';
import { applyServerErrors } from '@/utils/formErrors';
import { resetPasswordSchema, type ResetPasswordValues } from '@/features/auth/schemas';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [formError, setFormError] = useState('');

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ResetPasswordValues) =>
      authApi.resetPassword({ token, password: values.password }),
    onSuccess: () => navigate(`${paths.login}?reset=1`, { replace: true }),
    onError: (error) => setFormError(applyServerErrors(error, form.setError, ['password'])),
  });

  if (!token) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6">Invalid reset link</Typography>
        <Alert severity="error">This password reset link is missing or malformed.</Alert>
        <Link component={RouterLink} to={paths.forgotPassword} variant="body2" underline="hover">
          Request a new link
        </Link>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} component="form" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
      <Box>
        <Typography variant="h6">Choose a new password</Typography>
        <Typography variant="body2" color="text.secondary">
          Use at least 12 characters with upper, lower, and a number.
        </Typography>
      </Box>
      {formError && <Alert severity="error">{formError}</Alert>}
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        autoFocus
        error={!!form.formState.errors.password}
        helperText={form.formState.errors.password?.message}
        {...form.register('password')}
      />
      <TextField
        label="Confirm password"
        type="password"
        autoComplete="new-password"
        error={!!form.formState.errors.confirmPassword}
        helperText={form.formState.errors.confirmPassword?.message}
        {...form.register('confirmPassword')}
      />
      <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}>
        {mutation.isPending ? 'Saving…' : 'Reset password'}
      </Button>
    </Stack>
  );
}
